import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../util/API';

const initialState = {
    orders: [],
    order: null,
    status: 'idle',
    error: null,
    updateStatus: 'idle',
    updateError: null,
    deleteStatus: 'idle',
    deleteError: null,
};

// Async thunk to fetch all orders
export const fetchOrders = createAsyncThunk(
    'order/fetchOrders',
    async (_, { rejectWithValue }) => {
        try {
            // Admin panel must use admin endpoint
            const response = await API.get('/order/admin');
            return response.data?.data ?? response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Async thunk to fetch a single order by ID
export const fetchOrderById = createAsyncThunk(
    'order/fetchOrderById',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await API.get(`/order/${orderId}`);
            return response.data?.data ?? response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// Async thunk to create a new order
export const createOrder = createAsyncThunk(
    'order/createOrder',
    async (orderData, { rejectWithValue }) => {
        try {
            const response = await API.post('/order', orderData);
            return response.data?.data ?? response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// Admin: approve/update an order (uses admin PUT endpoint)
export const approveOrder = createAsyncThunk(
    'order/approveOrder',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await API.post(`/order/${id}/approve`);
            return response.data?.data ?? response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Admin: delete a single order
export const deleteOrder = createAsyncThunk(
    'order/deleteOrder',
    async (id, { rejectWithValue }) => {
        try {
            await API.delete(`/order/${id}`);
            return { id };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// Admin: bulk delete (client-side loop)
export const deleteOrdersBulk = createAsyncThunk(
    'order/deleteOrdersBulk',
    async (ids, { rejectWithValue }) => {
        try {
            const list = Array.isArray(ids) ? ids.filter(Boolean).map(String) : [];
            if (!list.length) return { deletedIds: [], failed: [] };

            const results = await Promise.allSettled(
                list.map((id) => API.delete(`/order/${id}`))
            );

            const deletedIds = [];
            const failed = [];
            results.forEach((r, idx) => {
                const id = list[idx];
                if (r.status === 'fulfilled') deletedIds.push(id);
                else failed.push({ id, error: r.reason });
            });

            return { deletedIds, failed };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        clearOrder(state) {
            state.order = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchOrders
            .addCase(fetchOrders.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchOrders.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.orders = action.payload;
            })
            .addCase(fetchOrders.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // fetchOrderById
            .addCase(fetchOrderById.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchOrderById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.order = action.payload;
            })
            .addCase(fetchOrderById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // createOrder
            .addCase(createOrder.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.orders.push(action.payload);
                state.order = action.payload;
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            // approveOrder
            .addCase(approveOrder.pending, (state) => {
                state.updateStatus = 'loading';
                state.updateError = null;
            })
            .addCase(approveOrder.fulfilled, (state, action) => {
                state.updateStatus = 'succeeded';
                const updated = action.payload;
                const updatedId = String(updated?._id || updated?.id || '');

                if (updatedId) {
                    const idx = state.orders.findIndex((o) => String(o?._id || o?.id || '') === updatedId);
                    if (idx >= 0) state.orders[idx] = updated;
                }

                if (state.order && String(state.order?._id || state.order?.id || '') === updatedId) {
                    state.order = updated;
                }
            })
            .addCase(approveOrder.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.updateError = action.payload;
            })

            // deleteOrder
            .addCase(deleteOrder.pending, (state) => {
                state.deleteStatus = 'loading';
                state.deleteError = null;
            })
            .addCase(deleteOrder.fulfilled, (state, action) => {
                state.deleteStatus = 'succeeded';
                const deletedId = String(action.payload?.id || '');
                if (deletedId) {
                    state.orders = (state.orders || []).filter((o) => String(o?._id || o?.id || '') !== deletedId);
                    if (state.order && String(state.order?._id || state.order?.id || '') === deletedId) {
                        state.order = null;
                    }
                }
            })
            .addCase(deleteOrder.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.deleteError = action.payload;
            })

            // deleteOrdersBulk
            .addCase(deleteOrdersBulk.pending, (state) => {
                state.deleteStatus = 'loading';
                state.deleteError = null;
            })
            .addCase(deleteOrdersBulk.fulfilled, (state, action) => {
                state.deleteStatus = 'succeeded';
                const deletedIds = Array.isArray(action.payload?.deletedIds) ? action.payload.deletedIds : [];
                const deletedSet = new Set(deletedIds.map(String));
                if (deletedSet.size) {
                    state.orders = (state.orders || []).filter((o) => !deletedSet.has(String(o?._id || o?.id || '')));
                    if (state.order && deletedSet.has(String(state.order?._id || state.order?.id || ''))) {
                        state.order = null;
                    }
                }

                const failed = Array.isArray(action.payload?.failed) ? action.payload.failed : [];
                if (failed.length) state.deleteError = { failed };
            })
            .addCase(deleteOrdersBulk.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.deleteError = action.payload;
            });
    },
});

export const { clearOrder } = orderSlice.actions;

export default orderSlice.reducer;